import { useInfiniteQuery } from "@tanstack/react-query";
import { ScreenFilters, screenService } from "../api/services/screenService";
import type { Screen, ScreenResult } from "../schemas/screenSchema";
import { useScreens } from "./useScreens";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type ScreensInfiniteFilters = Omit<ScreenFilters, "page" | "pageSize"> & {
  pageSize?: number;
};

export const useScreensInfinite = (filters: ScreensInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;

  const {
    createScreen,
    updateScreen,
    resetScreen,
    deleteScreens,
  } = useScreens({}, undefined, { enabled: false });

  const query = useInfiniteQuery<ScreenResult, Error>({
    queryKey: ["screens", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      screenService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
    // Safety net for the status badges: live updates come via the
    // screenStatusChanged subscription, but a silently-dead WebSocket would
    // otherwise leave the list stale indefinitely. Bounds staleness to 60s.
    refetchInterval: 60_000,
    // App.tsx sets a global 5-minute staleTime, and refetchInterval only runs
    // while a query is observed. Re-applying a filter or sort seen in the last
    // 5 minutes would otherwise show that cached result with no network call.
    // The status subscription only patches rows already in that result, so a
    // screen that newly matched the filter stayed missing until the next poll
    // or a page reload. Always refetch when the key becomes active again.
    staleTime: 0,
  });

  const screens: Screen[] =
    query.data?.pages.flatMap((p) => p.screens) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    screens,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    createScreen,
    updateScreen,
    resetScreen,
    deleteScreens,
  };
};
